import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, map, Observable, of, take } from 'rxjs';
import * as authReducer from '../store/auth-state/auth.reducer';
import { AuthState } from '../store/app/app.states';
import { CurrentUser } from '../store/auth-state/auth.model';
import { AppFullRoutes, AppRoutesEnum } from '../enum/appRoutes';
import { PermissionService } from '../services/commonServices/permission.service';
import { AuthService } from '../../modules/authentication/auth.service';

@Injectable({ providedIn: 'root' })
export class RequireAuthGuard implements CanActivate, CanActivateChild {
  constructor(
    private readonly router: Router,
    private readonly store: Store<AuthState>,
    private readonly permissionService: PermissionService,
    private readonly auth: AuthService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.checkAccess(state);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.checkAccess(state);
  }

  private checkAccess(
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {

    //  No token → start OAuth login
    if (!this.auth.isLoggedIn) {
      this.auth.login(); // OAuth redirect
      return of(false);
    }

    return this.store.select(authReducer.getAuthState).pipe(
      //  WAIT until auth bootstrap finishes
      map(auth => {
        // still loading user info
        if (!auth.initialized) {
          return true;
        }

        const user = auth.currentUser;

        if (!user) {
          this.auth.login(); // token expired or invalid
          return false;
        }

        const permissions = user.roles?.[0]?.permissions ?? [];

        if (!permissions.length) {
          return this.router.parseUrl('/no-access');
        }

        this.permissionService.setPermissions(permissions);
        return true;
      }),
      take(1),
      catchError(() => of(true))
    );
  }
}
